import { useEffect, useRef, useState } from "react";
import { supabase } from "./lib/supabase";
import { downloadWpdsPdf } from "./lib/wpdsPdf";

type WeldFields = {
  process?: string | null;
  thickness_mm?: number | null;
  base_material?: string | null;
  filler?: string | null;
  joint_category?: string | null;
  penetration?: "CJP" | "PJP" | null;
  position?: "F" | "H" | "V" | "O" | null;
  backing?: string | null;
  gap?: number | null;
  angle?: number | null;
};
type Interpretation = { fields: WeldFields; missing: string[]; mode: "live-ai" | "offline-demo" };

function offlineInterpret(request: string): Interpretation {
  const s = request.replace(/\s+/g, " ").trim();
  const pick = (re: RegExp) => s.match(re)?.[1] ?? null;
  const process = pick(/\b(SMAW|MCAW|FCAW-G|FCAW-S)\b/i);
  const thickness = pick(/\b(\d+(?:\.\d+)?)\s*(?:mm|millimet(?:re|er)s?)\b/i);
  const base = pick(/\b(CSA\s*G40\.21\s*\d{3}[A-Z]*)\b/i);
  const filler = pick(/\b(E\s*\d{4}(?:[-\w]*)?)\b/i);
  const position = pick(/\b(flat|horizontal|vertical|overhead)\b/i);
  const fields: WeldFields = {
    process: process?.toUpperCase() || null,
    thickness_mm: thickness ? Number(thickness) : null,
    base_material: base?.replace(/\s+/g, " ").toUpperCase() || null,
    filler: filler?.replace(/\s+/g, "").toUpperCase() || null,
    position: position ? ({flat:"F", horizontal:"H", vertical:"V", overhead:"O"} as const)[position.toLowerCase() as "flat"|"horizontal"|"vertical"|"overhead"] : null,
    joint_category: /\bbutt\b/i.test(s) ? "Butt" : /\btee\b/i.test(s) ? "Tee" : /\bcorner\b/i.test(s) ? "Corner" : null,
    penetration: /\b(CJP|complete joint penetration|full penetration)\b/i.test(s) ? "CJP" : /\b(PJP|partial joint penetration)\b/i.test(s) ? "PJP" : null,
    backing: /\bsteel backing|backing (?:bar|strip)\b/i.test(s) ? "Steel backing" : null,
    gap: null, angle: null,
  };
  return {
    fields,
    missing: [
      ...(!fields.penetration ? ["CJP or PJP penetration"] : []),
      "Specific applicable joint detail and its dimensions",
      "Backing dimensions",
      "WPS reference and welding parameters",
      "Preheat, interpass temperature and electrode diameter",
      "Standards verification and qualified review"
    ],
    mode: "offline-demo"
  };
}

export default function WpdsStudio({ preview = false }: { preview?: boolean }) {
  const frame = useRef<HTMLIFrameElement | null>(null);
  const [frameHeight, setFrameHeight] = useState(1200);
  const [serviceStatus, setServiceStatus] = useState(preview ? "Offline demonstration" : "Authenticated AI service");

  useEffect(() => {
    const iframe = frame.current;
    if (!iframe) return;
    let remove: (() => void) | undefined;

    const install = () => {
      remove?.();
      const doc = iframe.contentDocument;
      if (!doc) return;

      // The original WPDS v1.5 is a full-viewport editor (body overflow:hidden,
      // fixed-height grid, independently scrolling panels). Embedded in ArcVault,
      // those rules create three competing scrollbars and hide the lower fields.
      // Use one document scroll, and grow the iframe to its real content height.
      const flowStyle = doc.createElement("style");
      flowStyle.id = "arcvault-embedded-single-scroll";
      flowStyle.textContent = `
        @media screen {
          html, body { height:auto !important; min-height:0 !important; overflow:visible !important; }
          main#studioMain {
            height:auto !important; min-height:0 !important;
            align-items:start !important;
          }
          main#studioMain > .workspace, main#studioMain > .context-panel {
            height:auto !important; max-height:none !important; min-height:0 !important;
            overflow:visible !important;
          }
          main#studioMain .step-scroll, main#studioMain .context-scroll {
            flex:none !important; height:auto !important; max-height:none !important;
            min-height:0 !important; overflow:visible !important;
            overscroll-behavior:auto !important;
          }
          main#studioMain .all-checks #checks {
            max-height:none !important; overflow:visible !important;
          }
          @media (max-width:760px) {
            main#studioMain > .workspace { height:auto !important; min-height:0 !important; }
          }
        }
      `;
      doc.head.appendChild(flowStyle);

      let measureFrameId = 0;
      let overlayOpen = false;
      let scrollBeforePreview: number | null = null;
      const measure = () => {
        const body = doc.body;
        const html = doc.documentElement;
        if (!body || !html) return;
        const contentHeight = Math.ceil(Math.max(
          body.scrollHeight, html.scrollHeight,
          body.getBoundingClientRect().height
        )) + 8;
        if (Number.isFinite(contentHeight) && contentHeight > 0) {
          setFrameHeight(current => Math.abs(current - contentHeight) > 3 ? contentHeight : current);
        }
      };
      const scheduleMeasure = () => {
        if (measureFrameId) cancelAnimationFrame(measureFrameId);
        measureFrameId = requestAnimationFrame(() => { measureFrameId = 0; measure(); });
      };
      const resizeObserver = new ResizeObserver(scheduleMeasure);
      resizeObserver.observe(doc.body);
      const studioMain = doc.getElementById("studioMain");
      if (studioMain) resizeObserver.observe(studioMain);
      const onMutation = () => {
        const open = doc.getElementById("wpdsOverlay")?.classList.contains("open") ?? false;
        if (open && !overlayOpen) {
          scrollBeforePreview = window.scrollY;
          iframe.scrollIntoView({ behavior: "auto", block: "start" });
        } else if (!open && overlayOpen && scrollBeforePreview !== null) {
          window.scrollTo({ top: scrollBeforePreview, behavior: "auto" });
          scrollBeforePreview = null;
        }
        overlayOpen = open;
        scheduleMeasure();
      };
      const mutationObserver = new MutationObserver(onMutation);
      mutationObserver.observe(doc.body, {
        childList:true, subtree:true, attributes:true, attributeFilter:["class", "open"]
      });
      doc.addEventListener("load", scheduleMeasure, true);
      void doc.fonts?.ready.then(scheduleMeasure);
      scheduleMeasure();
      const cleanupLayout = () => {
        if (measureFrameId) cancelAnimationFrame(measureFrameId);
        mutationObserver.disconnect();
        resizeObserver.disconnect();
        doc.removeEventListener("load", scheduleMeasure, true);
        flowStyle.remove();
      };
      remove = cleanupLayout;

      const el = (id: string) => doc.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | HTMLButtonElement | HTMLDivElement | null;
      const requestInput = el("aiRequest") as HTMLTextAreaElement | null;
      const resultElement = el("aiResult");
      const runButton = el("aiRun") as HTMLButtonElement | null;
      const applyButton = el("aiApply") as HTMLButtonElement | null;
      if (!requestInput || !resultElement || !runButton || !applyButton) {
        setServiceStatus("Builder loaded, AI controls unavailable");
        return;
      }

      let last: Interpretation | null = null;
      const addText = (tag: string, message: string, css?: string) => {
        const node = doc.createElement(tag);
        node.textContent = message;
        if (css) node.className = css;
        resultElement.appendChild(node);
      };
      const report = (message: string) => { resultElement.replaceChildren(); addText("p", message); };
      const show = (parsed: Interpretation) => {
        resultElement.replaceChildren();
        addText("strong", parsed.mode === "live-ai" ? "OpenAI interpretation · draft only" : "OFFLINE RULE-BASED DEMONSTRATION · not AI");
        const values = Object.entries(parsed.fields).filter(([, v]) => v !== null && v !== undefined && v !== "");
        const summary = doc.createElement("div");
        summary.style.margin = "8px 0";
        for (const [key,value] of values) {
          const item = doc.createElement("span");
          item.className = "ai-chip";
          item.textContent = key + ": " + String(value);
          summary.appendChild(item);
        }
        resultElement.appendChild(summary);
        addText("p", "Still needed: " + parsed.missing.join("; "));
        addText("p", "No CSA prequalification finding has been made. Joint designation, penetration and geometry remain subject to verification.", "ai-note");
        applyButton.disabled = false;
      };
      const setField = (id: string, value?: string | number | null) => {
        if (value === null || value === undefined) return;
        const field = el(id) as HTMLInputElement | HTMLSelectElement | null;
        if (!field) return;
        if (field.tagName === "SELECT" &&
            !Array.from((field as HTMLSelectElement).options).some(option => option.value === String(value))) return;
        field.value = String(value);
        field.dispatchEvent(new Event("input", { bubbles: true }));
        field.dispatchEvent(new Event("change", { bubbles: true }));
      };
      const apply = () => {
        if (!last) return;
        const f = last.fields;
        setField("process", f.process);
        if (f.penetration) doc.querySelector<HTMLButtonElement>('.tab[data-kind="' + f.penetration + '"]')?.click();
        setField("category", f.joint_category);
        setField("position", f.position);
        setField("thickness", f.thickness_mm);
        setField("basemat", f.base_material);
        // Part II base material remains blank unless explicitly identified.
        setField("filler", f.filler);
        setField("backing", f.backing);
        setField("gap", f.gap == null ? "" : f.gap);
        setField("angle", f.angle);
        const existingNotice = doc.getElementById("arcvault-joint-selection-notice");
        existingNotice?.remove();
        if (!f.penetration) {
          const field = doc.querySelector<HTMLButtonElement>('.tab[data-kind="CJP"]')?.closest(".field");
          if (field) {
            const notice = doc.createElement("div");
            notice.id = "arcvault-joint-selection-notice";
            notice.setAttribute("role", "alert");
            notice.style.cssText = "margin:8px 0;padding:12px;border:2px solid #d59c2d;background:#fff4da;color:#603a00;border-radius:7px;font-weight:600;font-size:13px;line-height:1.5";
            notice.textContent = "PENETRATION NOT SPECIFIED. The displayed CJP tab and joint detail are unverified builder defaults, NOT an AI selection or prequalification finding. Confirm CJP or PJP, select a joint detail, and verify dimensions against CSA W59 before using this draft.";
            field.appendChild(notice);
            const acknowledge = (event: Event) => {
              const button = (event.target as Element | null)?.closest("button[data-kind]");
              if (button) {
                notice.textContent = "Penetration manually selected. Verify the joint designation and all governing CSA rules before claiming prequalification.";
                field.removeEventListener("click", acknowledge);
              }
            };
            field.addEventListener("click", acknowledge, { once: true });
          }
        }
        addText("p", "Known fields applied. Unspecified root opening was cleared. Confirm penetration, applicable joint geometry, welding conditions, and engineering review before drafting.", "ai-note");
      };
      const interpret = async () => {
        const request = requestInput.value.trim();
        if (!request || request.length > 2500) { report("Enter a welding request (maximum 2,500 characters)."); return; }
        runButton.disabled = true;
        applyButton.disabled = true;
        last = null;
        report("Extracting technical information…");
        try {
          if (preview) {
            last = offlineInterpret(request);
          } else {
            const { data: { user }, error: authError } = await supabase.auth.getUser();
            if (authError || !user) throw new Error("Sign in to an authorized ArcVault company account to use AI.");
            const { data, error } = await supabase.functions.invoke("interpret-weld-request", { body: { request } });
            if (error) {
              const responseError = "context" in error && error.context instanceof Response
                ? await error.context.clone().json().catch(() => null) as { error?: string } | null : null;
              throw new Error(responseError?.error || "AI request failed. Verify the Supabase function, OpenAI key and billing.");
            }
            if (data?.error) throw new Error(String(data.error));
            if (!data || typeof data.fields !== "object" || data.fields === null || !Array.isArray(data.missing))
              throw new Error("The AI returned an invalid structured response.");
            last = { fields: data.fields as WeldFields, missing: data.missing as string[], mode: "live-ai" };
          }
          show(last);
          setServiceStatus(last.mode === "live-ai" ? "Live AI interpreted request" : "Offline demonstration");
        } catch (err) {
          const message = err instanceof Error ? err.message : "Unknown AI error";
          report("AI unavailable: " + message + ". No fields were changed.");
          setServiceStatus("AI unavailable; no procedure generated");
        } finally { runButton.disabled = false; }
      };

      // Capture original HTML button events before its legacy handlers. All AI requests
      // are sent through the authenticated React/Supabase bridge, not the public HTML.
      const onClick = (event: MouseEvent) => {
        const button = (event.target as Element | null)?.closest("button");
        if (!button || (button.id !== "aiRun" && button.id !== "aiApply" && button.id !== "exportPDF")) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (button.id === "aiRun") void interpret();
        else if (button.id === "aiApply") apply();
        else {
          const exportButton = button as HTMLButtonElement;
          exportButton.disabled = true;
          const previousText = exportButton.textContent;
          exportButton.textContent = "Preparing PDF…";
          void downloadWpdsPdf(doc).catch((error: unknown) => {
            console.error("WPDS PDF export failed", error);
            // Keep the original, browser-native print/save-as-PDF path available.
            doc.body.classList.add("printSheet");
            (doc.getElementById("wpdsSheet") as HTMLElement | null)?.style.setProperty("transform", "scale(.75)");
            iframe.contentWindow?.addEventListener("afterprint", () => doc.body.classList.remove("printSheet"), { once: true });
            iframe.contentWindow?.print();
            setServiceStatus("PDF download unavailable; browser print opened");
          }).finally(() => {
            exportButton.textContent = previousText;
            exportButton.disabled = false;
          });
        }
      };
      doc.addEventListener("click", onClick, true);

      // The standalone HTML endpoint field is intentionally disabled for integrated mode.
      const config = el("aiConfig") as HTMLButtonElement | null;
      if (config) config.style.display = "none";
      const settings = el("aiSettings");
      if (settings) settings.style.display = "none";
      const offlineNote = doc.querySelector(".ai-note");
      if (offlineNote) offlineNote.textContent = preview
        ? "Offline rule-based demo only. No AI connection or CSA engineering approval."
        : "Secure AI runs through ArcVault authentication. The AI extracts fields but does not certify prequalification.";
      report("Enter a welding request, then select Interpret request. No joint compliance is assumed.");
      setServiceStatus(preview ? "Offline demonstration" : "Authenticated AI service ready");
      remove = () => { doc.removeEventListener("click", onClick, true); cleanupLayout(); };
    };

    iframe.addEventListener("load", install);
    if (iframe.contentDocument?.readyState === "complete") install();
    return () => { iframe.removeEventListener("load", install); remove?.(); };
  }, [preview]);

  return <section className="content">
    <div className="page-head">
      <div><p className="eyebrow">PROCEDURE CONTROL</p><h1>WPDS Builder</h1>
        <p>AI-assisted drafting with controlled engineering review.</p></div>
      <span role="status" style={{fontSize:12, alignSelf:"center"}}>{serviceStatus}</span>
    </div>
    <div style={{border:"1px solid #d9e0e7", borderRadius:14, overflow:"hidden", background:"white"}}>
      <iframe ref={frame} title="ArcVault WPDS Builder" src="/wpds-builder.html"
        style={{display:"block", width:"100%", height:frameHeight, minHeight:650, border:0}} />
    </div>
    <p style={{fontSize:12, opacity:0.75, paddingTop:12}}>
      Draft only. CSA joint selection, process limitations, welding parameters and CWB acceptance require independent verification.
    </p>
  </section>;
}
